import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { EmptyState, Segmented, TableSkeleton } from "../../../components/ui";
import { getPaymentsChart } from "../../../services/paymentsLedger";
import { formatMoney, TYPE_COLORS, TYPES } from "./shared";

const FAILED_COLOR = "#ef5d8f";
const METHOD_LABELS = {
  stripe: "Card",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  cinetpay: "Mobile Money · CinetPay",
  paystack: "Mobile Money · Paystack",
  pawapay: "Mobile Money · pawaPay",
};
const METHOD_COLORS = ["#1176BC", "#CDAE6E", "#22c58b", "#8b7cf6", "#f5a524", "#5b6475"];
const muted = { colors: "#9b9ba6" };

const dateLabel = (key) =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString([], { day: "numeric", month: "short", timeZone: "UTC" });

/**
 * Stacked columns of the successful payments per type; in the count view the failures are stacked
 * on top in red, so a column is every attempt of the period.
 */
export const evolutionChart = (data, { metric, t }) => {
  const types = Object.keys(TYPE_COLORS).filter((type) => data.byType[type]);
  const value = (bucket, type) => bucket.types[type]?.[metric] || 0;
  const money = (n) => formatMoney(n, data.currency);
  const series = types.map((type) => ({
    name: t(TYPES[type]?.label || "Other"),
    data: data.buckets.map((bucket) => value(bucket, type)),
  }));
  const colors = types.map((type) => TYPE_COLORS[type]);
  if (metric === "count") {
    series.push({ name: t("Failed"), data: data.buckets.map((bucket) => bucket.failed) });
    colors.push(FAILED_COLOR);
  }
  const weekly = data.unit === "week";
  // One date out of `step` on the axis, fewer on a phone
  const maxLabels = typeof window !== "undefined" && window.innerWidth < 600 ? 5 : 12;
  const step = Math.max(1, Math.ceil(data.buckets.length / maxLabels));
  const shown = new Set(data.buckets.filter((bucket, i) => (data.buckets.length - 1 - i) % step === 0).map((bucket) => bucket.date));
  const format = metric === "amount" ? money : (n) => Math.round(n).toLocaleString();
  return {
    series,
    options: {
      chart: { id: "payments-evolution", stacked: true, background: "transparent", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", animations: { enabled: false } },
      colors,
      plotOptions: { bar: { columnWidth: data.buckets.length > 40 ? "80%" : "55%", borderRadius: 2 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
      legend: { position: "top", horizontalAlign: "right", labels: muted },
      labels: data.buckets.map((bucket) => bucket.date),
      xaxis: {
        type: "category",
        labels: { style: muted, rotate: 0, hideOverlappingLabels: false, formatter: (value) => (shown.has(value) ? dateLabel(value) : "") },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: { min: 0, forceNiceScale: true, labels: { style: muted, formatter: format } },
      tooltip: {
        theme: "dark",
        shared: true,
        intersect: false,
        x: { formatter: (value, { dataPointIndex }) => {
          const key = data.buckets[dataPointIndex]?.date;
          return key ? (weekly ? `${t("Week of")} ${dateLabel(key)}` : dateLabel(key)) : "";
        } },
        y: { formatter: format },
      },
    },
  };
};

/** Donut of the successful payments per payment method. */
export const methodsChart = (data, { metric, t }) => {
  const rows = data.byMethod.filter((row) => row[metric] > 0);
  const format = metric === "amount" ? (n) => formatMoney(n, data.currency) : (n) => Math.round(n).toLocaleString();
  return {
    rows,
    series: rows.map((row) => row[metric]),
    options: {
      chart: { id: "payments-methods", background: "transparent", fontFamily: "inherit" },
      colors: METHOD_COLORS,
      labels: rows.map((row) => t(METHOD_LABELS[row.method] || row.method)),
      dataLabels: { enabled: false },
      stroke: { show: false, width: 0 },
      legend: { show: false },
      tooltip: { theme: "dark", y: { formatter: format } },
      plotOptions: { pie: { donut: { size: "70%" } } },
    },
  };
};

/**
 * Evolution of the payments with the filters of the page (every status): per type, failures,
 * payment methods and statuses. Amounts in one currency at a time.
 */
const PaymentsChart = ({ filters }) => {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState("amount");
  const [currency, setCurrency] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const datesSet = Boolean(filters.from || filters.to);

  const key = JSON.stringify({ ...filters, status: undefined, thanked: undefined, days, currency });
  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    getPaymentsChart(JSON.parse(key))
      .then((result) => {
        if (!alive) return;
        if (result && Array.isArray(result.buckets)) setData(result);
        else setError("update");
      })
      .catch((failure) => {
        if (!alive) return;
        const status = failure?.response?.status;
        // 404: the API has not been restarted with the chart route yet
        setError(status === 404 ? "update" : status === 401 || status === 403 ? "access" : "failed");
      });
    return () => {
      alive = false;
    };
  }, [key, attempt]);

  const empty = data && !data.byStatus.SUCCESS && !data.byStatus.FAILED && !data.byStatus.PENDING;
  const evolution = data && !empty ? evolutionChart(data, { metric, t }) : null;
  const methods = data && !empty ? methodsChart(data, { metric, t }) : null;
  const statusTotal = data ? data.byStatus.SUCCESS + data.byStatus.FAILED + data.byStatus.PENDING : 0;
  const successRate = statusTotal ? Math.round((data.byStatus.SUCCESS / (data.byStatus.SUCCESS + data.byStatus.FAILED || 1)) * 100) : null;

  return (
    <section className="ui-card ledger-chart">
      <div className="ledger-chart-head">
        <h2 className="ledger-chart-title">
          {data?.unit === "week" ? t("Payments per week") : t("Payments per day")}
        </h2>
        <div className="ledger-chart-controls">
          {datesSet ? (
            <span className="ui-hint">{t("Period of the date filters")}</span>
          ) : (
            <Segmented
              label={t("Period")}
              value={days}
              onChange={setDays}
              options={[
                { value: 7, label: t("7 days") },
                { value: 30, label: t("30 days") },
                { value: 90, label: t("90 days") },
                { value: 365, label: t("1 year") },
              ]}
            />
          )}
          <Segmented
            label={t("Show")}
            value={metric}
            onChange={setMetric}
            options={[
              { value: "amount", label: t("Amount") },
              { value: "count", label: t("Number") },
            ]}
          />
          {metric === "amount" && data?.currencies?.length > 1 && (
            <select className="ui-input ledger-chart-currency" aria-label={t("Currency")} value={data.currency} onChange={(e) => setCurrency(e.target.value)}>
              {data.currencies.map((row) => (
                <option key={row.currency} value={row.currency}>
                  {row.currency}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error ? (
        <EmptyState icon="ri-error-warning-line">
          {error === "update"
            ? t("The server does not have the chart yet: update and restart the API.")
            : error === "access"
            ? t("Your account cannot read the payments.")
            : t("The chart could not be loaded.")}{" "}
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setAttempt((n) => n + 1)}>
            {t("Retry")}
          </button>
        </EmptyState>
      ) : !data ? (
        <TableSkeleton rows={4} />
      ) : empty ? (
        <EmptyState icon="ri-bar-chart-line">{t("No payment over this period")}</EmptyState>
      ) : (
        <>
          {metric === "amount" && data.currencies.length > 1 && (
            <p className="ui-hint ledger-chart-note">
              {t("Amounts in {{currency}} only; the other currencies are counted in the « Number » view.", { currency: data.currency })}
            </p>
          )}
          <div className="ledger-chart-grid">
            <div className="ledger-chart-main">
              <Chart type="bar" height={300} options={evolution.options} series={evolution.series} />
            </div>
            <div className="ledger-chart-side">
              <h3>{t("Payment methods")}</h3>
              {methods.rows.length ? (
                <>
                  <Chart type="donut" height={170} options={methods.options} series={methods.series} />
                  <ul className="ledger-chart-legend">
                    {methods.rows.map((row, i) => (
                      <li key={row.method}>
                        <span className="ledger-chart-dot" style={{ background: METHOD_COLORS[i % METHOD_COLORS.length] }}></span>
                        <span>{t(METHOD_LABELS[row.method] || row.method)}</span>
                        <strong>{metric === "amount" ? formatMoney(row.amount, data.currency) : row.count.toLocaleString()}</strong>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="ui-hint">—</p>
              )}
              <h3>{t("Statuses")}</h3>
              <ul className="ledger-chart-legend">
                {[
                  ["SUCCESS", t("Succeeded"), "#22c58b"],
                  ["FAILED", t("Failed"), FAILED_COLOR],
                  ["PENDING", t("Pending"), "#f5a524"],
                ].map(([status, label, color]) => (
                  <li key={status}>
                    <span className="ledger-chart-dot" style={{ background: color }}></span>
                    <span>{label}</span>
                    <strong>{data.byStatus[status].toLocaleString()}</strong>
                  </li>
                ))}
              </ul>
              {successRate !== null && (
                <p className="ui-hint">{t("Success rate: {{rate}} %", { rate: successRate })}</p>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default PaymentsChart;
